import { useCallback, useEffect, createRef } from 'react';
import { useQuery, useMutation } from 'react-query';
import axios, { AxiosError } from 'axios';
import { useState } from 'react';
import { getHashSearchParam, setTGUsername } from '../../utils/utils';
import fotlogin from '../../assets/fotlogin.svg';
import errorsvg from '../../assets/error.svg';
import { compare } from 'compare-versions';
import { CenterContainer, StyledContainer, StyledFormContainer, StyledLoadingContainer } from './styled';
import { useParticleJS } from './hook';
import { getReturnUrl } from './../../utils/utils';
import { getOIDCRequest, getSAMLRequest } from '@tigergraph/tools-models';
import { Button, Input, Label, PasswordInput, Spinner } from '@tigergraph/tiger-ui';

type LoginError = {
  error: boolean;
  message: string;
  results: Object;
};

type Response = {
  error: boolean;
  message: string;
  results: {
    isGlobalDesigner: boolean;
    isSuperUser: boolean;
    name: string;
    roles: {
      [key: string]: string[];
    };
    securityRecommendations: string[];
  };
};

type OIDCResponse = {
  error: boolean;
  message: string;
  results: {
    OIDCRequest: string;
    ssoBinding: string;
  };
};

type VersionResponse = {
  error: boolean;
  message: string;
  results: {
    tigergraph_version: string;
    is_community_edition: boolean;
  };
};

export default function Login() {
  useParticleJS();

  const formRef = createRef<HTMLFormElement>();

  const getAuthnRequestURL = '/api/auth/login';
  const logOutURL = '/api/auth/logout';
  const versionURL = '/api/version';

  const [username, setUserName] = useState(process.env.REACT_APP_TG_USERNAME);
  const [password, setPassword] = useState(getHashSearchParam('loggedOut') ? '' : process.env.REACT_APP_TG_PASSWORD);
  const [loading, setLoading] = useState(false);
  const autoLogin = process.env.REACT_APP_ENV === 'cloud' && !getHashSearchParam('loggedOut');
  const [oidcLoading, setOidcLoading] = useState(process.env.REACT_APP_ENV === 'cloud' && autoLogin);
  const [loginerror, setLoginerror] = useState(false);
  const [errormsg, setErrormsg] = useState('User authentication failed.');
  const [showPasswordExpired, setShowPasswordExpired] = useState(false);

  const versionResponse = useQuery('version', () => axios.get<VersionResponse>(versionURL));
  const version = versionResponse.data?.data?.results?.tigergraph_version;
  const isCommunityVersion = versionResponse.data?.data?.results?.is_community_edition;
  const supportOIDC = version && compare(version, '3.10.0', '>=');

  useQuery('LOGOUT', () => axios.post(logOutURL), {
    onSettled: () => {
      ssoResponse.mutate();
      oidcResponse.mutate();
    },
    retry: true,
    retryDelay(failureCount, error) {
      return Math.min(1000 * 2 ** failureCount, 30000);
    },
  });

  const ssoResponse = useMutation('SSO', () => getSAMLRequest().then((response) => response && (response.data as any)));

  const oidcResponse = useMutation(
    'OIDC',
    () => getOIDCRequest().then((response) => response && (response.data as OIDCResponse)),
    {
      onSuccess: (response) => {
        if (autoLogin) {
          onOIDCLogin(response);
        }
      },
      onError: () => {
        setOidcLoading(false);
      },
    }
  );

  const loginAPI = async () => {
    try {
      setLoading(true);
      const response = await axios.post<Response>(`${getAuthnRequestURL}`, {
        username,
        password,
      });
      if (response.data) {
        setTGUsername(response.data.results.name);
        const { securityRecommendations } = response.data.results;
        if (securityRecommendations) {
          window.localStorage.setItem('TigerGraphSecurityRecommendations', JSON.stringify(securityRecommendations));
        }
        let returnURL = getReturnUrl();
        window.location.replace(`${window.location.origin}${returnURL}`);
        setLoading(false);
      }
    } catch (error) {
      const err = error as AxiosError;
      const errdata = err.response?.data as LoginError;
      if (errdata) {
        if (errdata.message.toLowerCase().includes('password has expired')) {
          setShowPasswordExpired(true);
          setTGUsername(username || '');
        } else {
          setErrormsg(errdata.message);
        }
      }
      setLoading(false);
      setLoginerror(true);
    }
  };

  useEffect(() => {
    if (!loginerror) {
      setShowPasswordExpired(false);
    }
  }, [loginerror]);

  useEffect(() => {
    setLoginerror(false);
  }, [username, password]);

  function handlFormChange(type: string) {
    return (e) => {
      switch (type) {
        case 'username':
          setUserName(e.target.value);
          break;
        case 'password':
          setPassword(e.target.value);
          break;
        default:
          break;
      }
    };
  }

  async function ssoLogin() {
    formRef.current?.submit();
  }

  function onOIDCLogin(response?: OIDCResponse) {
    let oidcResponseRes = response || oidcResponse.data;
    let returnState = getHashSearchParam('returnURL') || '/apps';
    if (returnState === '/apps') {
      returnState = '/#' + returnState;
    }
    if (returnState.indexOf('/mlwb') !== -1) {
      returnState = `${sessionStorage.getItem('BASEURL')}/mlwb/`;
    } else {
      returnState = window.location.origin + returnState;
    }
    const state = btoa(returnState);
    if (oidcResponseRes) {
      // for BYOC, we replace the redirect_uri in the OIDCRequest to make it point to the controller
      let { OIDCRequest } = oidcResponseRes.results;
      const redirectUriRegex = /redirect_uri=([^&]+)/;
      const redirectUriMatch = OIDCRequest.match(redirectUriRegex);
      if (redirectUriMatch) {
        const originalRedirectUri = decodeURIComponent(redirectUriMatch[1]);
        if (
          originalRedirectUri.match(
            /tg-[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\.[a-zA-Z0-9.-]+\.(privatelink|i|byoc)/
          )
        ) {
          OIDCRequest = OIDCRequest.replace(
            redirectUriRegex,
            `redirect_uri=${encodeURIComponent(getControllerURL() + '/auth/oidc/callback')}`
          );
        }
      }
      const ssoOIDCRequest = OIDCRequest + `&state=${state}`;
      window.open(ssoOIDCRequest, '_self');
    }
  }

  function getControllerURL() {
    const cloudEnv = sessionStorage.getItem('CLOUDENV');

    if (cloudEnv === 'dev') {
      return 'https://api-v4.tgcloud-dev.com/controller';
    } else if (cloudEnv === 'uat') {
      return 'https://api-v4-uat.tgcloud-dev.com/controller';
    }
    return 'https://api.tgcloud.io/controller/v4';
  }

  /**
   * Retrieve error and return url.
   * Auto login if it is not logged out by user.
   *
   * @private
   * @memberof LoginComponent
   */
  const retrieveParamsFromRouteAndAutoLogin = useCallback(() => {
    const errMsg = getHashSearchParam('loginFailure');
    if (errMsg) {
      console.log(errMsg);
    }
    if (!getHashSearchParam('loggedOut') && process.env.REACT_APP_ENV === 'testdrive') {
      loginAPI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process.env.REACT_APP_ENV === 'cloud']);

  function onInputKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' && username !== '' && password !== '') {
      event.preventDefault();
      event.stopPropagation();
      loginAPI();
    }
  }

  useEffect(() => {
    retrieveParamsFromRouteAndAutoLogin();
  }, [retrieveParamsFromRouteAndAutoLogin]);

  return (
    <>
      {oidcLoading ? (
        <StyledLoadingContainer>
          <Spinner />
        </StyledLoadingContainer>
      ) : (
        <StyledContainer id="particles-js">
          <StyledFormContainer>
            <div className="pb-6 border-b border-[rgba(212,218,223,0.4)]">
              <CenterContainer>
                <img className="mx-auto" src={fotlogin} alt="" />
              </CenterContainer>
            </div>
            {process.env.REACT_APP_ENV === 'cloud' ? (
              <Button
                onClick={() => {
                  onOIDCLogin();
                }}
                className="w-full mt-2.5"
                size="lg"
              >
                Login with TigerGraph Cloud
              </Button>
            ) : (
              <>
                {ssoResponse.data ? (
                  <>
                    <Button size="lg" variant="outline" className="w-full mt-6" onClick={ssoLogin}>
                      Login With SSO
                    </Button>
                    <div className="border-b border-b-[rgba(212,218,223,0.4)] my-8 relative flex justify-center">
                      <div className="font-[Roboto] not-italic font-normal text-base leading-4 text-[#767676] bg-white mb-[-8px]">
                        or Login with username
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}
                <div className="h-[72px]">
                  <Label htmlFor="input" className="my-2">
                    Username
                  </Label>
                  <Input
                    aria-label="Username Input"
                    value={username}
                    onChange={handlFormChange('username')}
                    onKeyDown={onInputKeyDown}
                  />
                  {/* </FormControl> */}
                </div>
                <div style={{ height: '83px' }}>
                  <Label htmlFor="input" className="my-2">
                    Password
                  </Label>
                  <PasswordInput
                    aria-label="Password Input"
                    value={password}
                    autoComplete="off"
                    onChange={handlFormChange('password')}
                    onKeyDown={onInputKeyDown}
                  />
                </div>
                {loginerror && (
                  <div className="flex w-full items-center font-[Roboto] not-italic font-normal text-xs text-[#B93535] h-4 mt-[-2px] mb-[10px]">
                    <img className="mr-[2px]" src={errorsvg} alt="" />
                    {showPasswordExpired ? (
                      <span>
                        Your password has expired. Please <a href="/#/changepassword">click here</a> to change your
                        password.
                      </span>
                    ) : (
                      errormsg
                    )}
                  </div>
                )}
                <div className="flex w-full items-center justify-center">
                  <CenterContainer className="space-y-2">
                    <Button
                      aria-label="Login Button"
                      size="lg"
                      onClick={loginAPI}
                      type="submit"
                      disabled={!!(!username || !password) || loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="small" className="text-[var(--background)]" />
                          Logging in
                        </>
                      ) : (
                        'Login'
                      )}
                    </Button>
                    {process.env.REACT_APP_ENV !== 'cloud' && supportOIDC && oidcResponse.data && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          onOIDCLogin();
                        }}
                        size="lg"
                      >
                        Login with OIDC
                      </Button>
                    )}
                  </CenterContainer>
                  {ssoResponse.data && !isCommunityVersion ? (
                    <>
                      <form
                        className="w-0 h-0 absolute z-[-2] invisible"
                        id="loginform"
                        action={ssoResponse.data.results.ssoUrl}
                        ref={formRef}
                        method={ssoResponse.data.results.ssoSAMLBinding || 'POST'}
                      >
                        <input readOnly name="SAMLRequest" value={ssoResponse.data.results.SAMLRequest} />
                        <input readOnly name="SigAlg" value={ssoResponse.data.results.SigAlg} />
                        <input readOnly name="Signature" value={ssoResponse.data.results.Signature} />
                        <input readOnly name="RelayState" value={btoa(getReturnUrl())} />
                      </form>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              </>
            )}
          </StyledFormContainer>
        </StyledContainer>
      )}
    </>
  );
}
